import React from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { Section, FieldGrid, IdTile } from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';
import { showCurrency } from '../../utils';
import { paymentToLabel } from '../../utils/enumLabels';

const hasValue = (value) => value !== undefined && value !== null && value !== '';
const hasMoneyValue = (value) => hasValue(value) && Number(value) !== 0;

function formatAmount(contract, value) {
	if (!hasMoneyValue(value)) {
		return null;
	}
	const amount = Number(value);
	if (Number.isNaN(amount)) {
		return null;
	}
	const currency = showCurrency(contract.currency) || contract.currency || '';
	return `${currency} ${amount.toLocaleString('es-UY')}`.trim();
}

function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : null;
}

function ContractInfo({ contract }) {
	const { t } = useLabels();

	if (!contract) {
		return null;
	}

	const reajusteValue = contract.lastAdjustmentDate
		? `${contract.lastAdjustmentIpc ? `IPC ${contract.lastAdjustmentIpc}% · ` : ''}${formatDate(contract.lastAdjustmentDate)}`
		: null;

	const left = [
		{
			label: t('field.property'),
			value: contract.property ? (
				<Link to={`/AdminScreen/assets/asset/${contract.property._id || contract.property}`}>
					{contract.property.customId || contract.property.name}
				</Link>
			) : (
				t('msg.noPropertyAssigned')
			),
		},
		{ label: t('field.housingUnit'), value: contract.housingUnit, mono: true },
		{
			label: t('entity.tenant'),
			value: contract.owner ? (
				<Link to={`/AdminScreen/accounts/account/${contract.owner._id || contract.owner}`}>{contract.owner.name}</Link>
			) : (
				t('msg.noTenantAssigned')
			),
		},
		{ label: t('entity.assetOwner'), value: contract.property?.owner?.name },
		{ label: t('field.folder'), value: contract.folder },
		{ label: t('field.details'), value: contract.field, area: true },
		{ label: t('field.observations'), value: contract.observations, area: true },
	];

	const right = [
		{ label: t('field.periodInit'), value: formatDate(contract.periodInit), mono: true },
		{ label: t('field.periodEnd'), value: formatDate(contract.periodEnd), mono: true },
		{ label: t('field.currency'), value: contract.currency, mono: true },
		{ label: t('field.amount'), value: formatAmount(contract, contract.amount), mono: true },
		{ label: t('field.lastAdjustmentDate'), value: reajusteValue },
		{ label: t('field.collectionMethod'), value: contract.paymentMethod },
		{ label: t('form.paymentTo'), value: contract.paymentTo ? paymentToLabel(t, contract.paymentTo) : null },
		{
			label: t('form.paymentAccount'),
			value: contract.bankAccount
				? `${contract.bankAccount.bank} - ${contract.bankAccount.accountNumber} (${contract.bankAccount.currency})`
				: null,
		},
		...(contract.hasPreviousDebts
			? [
				{ label: t('field.previousBalance'), value: formatAmount(contract, contract.previousDebt), mono: true },
				{ label: t('field.previousBalanceDatePlain'), value: formatDate(contract.previousDebtDate?.date || contract.previousDebtDate), mono: true },
			]
			: []),
	];

	return (
		<Section title={t('title.details')}>
			<FieldGrid left={left} right={right} />
			<div className='tw:mt-4 tw:flex tw:items-center tw:gap-2 tw:border-t tw:border-border tw:pt-4'>
				{contract.responsible ? (
					<>
						<IdTile name={contract.responsible.name} size={22} shape='circle' />
						<span className='tw:text-[13.5px] tw:text-ink-0'>{contract.responsible.name}</span>
					</>
				) : (
					<span className='tw:text-[13.5px] tw:text-ink-3'>{t('msg.noResponsibleAssigned')}</span>
				)}
			</div>
		</Section>
	);
}

export default ContractInfo;
